<template>
  <div data-module="work-multi-image-text">
    <div class="module-layout" :class="layoutClasses">

      <!-- container for images -->
      <div class="image-container"
        :class="containerClasses"
        ref="imageContainer"
      >
        <div class="flex-wrapper-vert">

          <!-- container for first two images -->
          <div class="image-col-1">
            <div
              v-for="(n, idx) in 2" :key="idx"
              class="image" :class="`image-${idx}`"
              :style="imageStyles(data.images[idx])"
            />
          </div>
          <!-- container for 3rd image -->
          <template v-if="data.images.length > 2">
            <div class="image-col-2">
              <div class="image" :class="`image-2`" :style="imageStyles(data.images[2])" />
            </div>
          </template>
        </div>

      </div>

      <!-- container for content -->
      <div class="content-container"  :class="containerClasses">
        <markdown class="markdown">{{data.content}}</markdown>
      </div>
    </div>
  </div>
</template>


<script>
/*
  Work section MultiImageText module

  can have 2 or 3 images (only!)

  data record:
  {
    reversed: <boolean>
    images: [
      {
        src: <url> // url
        hAlign: <string>, // 'left', 'center', 'right'
        vAlign: <string>, // 'top', 'center', 'bottom'
      },
      {},
      {}
    ],
    content: <markdown>
  }
*/
// Lib
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';

import WorkMixin from './WorkMixin';

export default {

  name: 'work-multi-image-text',

  props: ['data'],

  mixins: [WorkMixin],

  data() {
    return {
      imageContentWidth: 0,
    };
  },

  computed: {

    layoutClasses() {
      return {
        reversed: this.isReversed,
      };
    },

    // return class string, eg 'desktop two', 'mobile one'
    containerClasses() {
      const { images } = this.data;
      const isReversed = this.isReversed;

      let classArr = [];
      if (this.isMobile) {
        classArr = classArr.concat(['mobile', 'one']);
      } else if (this.isTablet) {
        classArr = classArr.concat(['tablet', 'two']);
      } else {
        classArr = classArr.concat(['desktop']);
        if (images.length === 2) {
          classArr = classArr.concat(['two']);
        } else {
          classArr = classArr.concat(['three']);
        }
      }

      if (isReversed) {
        classArr = classArr.concat(['reversed']);
      }

      return classArr.join(' ');
    },

  },

  methods: {

    imageStyles(image) {
      const { imageContentWidth } = this;
      const { hAlign, vAlign, src } = image;

      // add defaults
      const hA = hAlign || 'center';
      const vA = vAlign || 'center';

      const rUrl = getResponsiveUrl(src, imageContentWidth);
      return {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `${hA} ${vA}`,
      };
    },

    measureLayout() {
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentWidth() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },
};
</script>


<style lang="scss" scoped>
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';

$layout--percentage: $layout--max-content-width-ng / $layout--optimal-desktop * 100%;

[data-module="work-multi-image-text"] {
  width: 100%;

  // media query props
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }

  /*
    setup image basics
  */

  .image {
    overflow: hidden;
    background-size: cover;
    background-position: bottom center;
    background-repeat: no-repeat;
  }

  .image-0 {
    width: 100%;
    padding-bottom: $dimension-4-3;
  }

  .image-1 {
    // square
    width: 58%;
    padding-bottom: 58%;
  }

  .image-2 {
    width: 100%;
    padding-bottom: $dimension-9-16;
  }


  .module-layout {
    // media query props
    @include ngGrid('mobile');

    @include ngMq($nbp--tablet, up) {
      @include ngGrid('tablet');
    }

    @include ngMq($nbp--desktop, up) {
      @include ngGrid('desktop');
    }

    .image-container.mobile,
    .image-container.mobile.reversed {
      // handle .module-layout grid
      grid-column: 1 / -1;
      grid-row: 1;
      margin: 0 rem($grid-padding-mobile * -1px); // pull to edges

      .image-col-1 {

        .image-1 {
          display: none;
        }
      }

      .image-col-2 {
        display: none;
      }
    }

    /*
      tablet two
    */

    .image-container.tablet {
      // handle .module-layout grid
      grid-column: 1 / span 6;
      grid-row: 1;
      margin-left: 0;
      margin-right: rem($grid-gap-tablet * -1px); // pull to right, column

      // flex, to allow content to center
      display: flex;
      flex-direction: column;
      justify-content: center;

      .image-col-1 {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-end;

        .image-1 {
          margin-top: rem(28px); // spacing between other image
        }

        .image-2 {
          display: none;
        }
      }

      .image-col-2 {
        display: none;
      }
    }

    .image-container.tablet.reversed {
      // handle .module-layout grid
      grid-column: 7 / span 6;
      margin-left: rem($grid-gap-tablet * -1px); // pull to right, column
      margin-right: 0;

      .image-col-1 {
        align-items: flex-start;
      }
    }

    /*
      desktop two
    */

    .image-container.desktop.two {
      // handle .module-layout grid
      grid-column: 2 / span 5;
      grid-row: 1;
      margin-left: 0;
      margin-right: rem($grid-gap-desktop * -1px); // pull to right, column

      // flex, to allow content to center
      display: flex;
      flex-direction: column;
      justify-content: center;

      .flex-wrapper-vert {

        display: block;

        .image-col-1 {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-end;

          .image-1 {
            margin-top: rem(38px); // spacing between other image
          }
        }

        .image-col-2 {
          display: none;
        }
      }
    }

    .image-container.desktop.two.reversed {
      // handle .module-layout grid
      grid-column: 7 / span 5;
      grid-row: 1;
      margin-left: rem($grid-gap-tablet * -1px); // pull to right, column
      margin-right: 0;

      .image-col-1 {
        align-items: flex-start;
      }
    }


    /*
      desktop three
    */

    .image-container.desktop.three {
      // handle .module-layout grid
      grid-column: 1 / span 6;
      grid-row: 1;
      margin-left: 0;
      margin-right: rem($grid-gap-desktop * -1px); // pull to right, column

      // flex, to allow content to center
      display: flex;
      flex-direction: column;
      justify-content: center;

      .flex-wrapper-vert {

        display: grid;
        grid-gap: rem($grid-gap-desktop * 1px);
        grid-template-columns: 1.8fr 1fr;

        .image-col-1 {
          grid-column: 1 / span 1;
          grid-row: 1;

          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-end;

          .image-1 {
            margin-top: rem(33px); // spacing between other image
          }
        }

        .image-col-2 {
          grid-column: 2 / span 1;
          grid-row: 1;
          display: block;
        }
      }
    }

    .image-container.desktop.three.reversed {
      // handle .module-layout grid
      grid-column: 7 / span 6;
      margin-left: rem($grid-gap-tablet * -1px); // pull to right, column
      margin-right: 0;

      // flex, to allow content to center
      display: flex;
      flex-direction: column;
      justify-content: center;

      .flex-wrapper-vert {

        display: grid;
        grid-gap: rem($grid-gap-desktop * 1px);
        grid-template-columns: 1fr 1.8fr;

        .image-col-1 {
          grid-column: 2 / span 1;

          align-items: flex-start;
        }

        .image-col-2 {
          grid-column: 1 / span 1;
        }
      }
    }


    /*
      content container
    */

    .content-container {
      text-align: left;

      // media query props
      // handle grid module-layout
      grid-column: 1 / span 6;
      grid-row: 2;
      margin-right: 0;
      margin-left: 0;

      @include ngMq($nbp--tablet, up) {
        grid-column: 8 / span 5;
        grid-row: 1;
      }

      @include ngMq($nbp--desktop, up) {
        grid-column: 8 / span 4;
        grid-row: 1;
        margin-left: 0;
        margin-right: rem($grid-gap-tablet * -1px); // pull to right, column
      }

      // flex, to allow content to center
      display: flex;
      flex-direction: column;
      justify-content: center;

      .markdown {
        width: 100%;
      }
    }

    .content-container.reversed {
      text-align: left;

      @include ngMq($nbp--tablet, up) {
        text-align: right;
        grid-column: 1 / span 5;
      }

      @include ngMq($nbp--desktop, up) {
        text-align: right;
        grid-column: 2 / span 4;
        margin-left: rem($grid-gap-tablet * -1px); // pull to left, column
        margin-right: 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/work/MultiImageText.vue